import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

import { apiKey } from "@/lib/api/changes"
import { apiFetch } from "@/lib/api/client"
import type { ApiPage, ApiResource } from "@/lib/api/types"
import type {
  Filing,
  FilingApprovalInput,
  FilingFilters,
  FilingPage,
  FilingStatus,
  FilingSubmissionInput,
  ApiFiling,
} from "@/types/filing"

export function toFiling(data: ApiFiling): Filing {
  return {
    id: data.id,
    form: data.form,
    formLabel: data.form_label,
    title: data.title,
    status: data.status,
    standing: data.standing,
    subject: data.subject
      ? {
          type: data.subject.type,
          id: data.subject.id,
          name: data.subject.name,
        }
      : null,
    appointmentId: data.appointment_id,
    reviewId: data.review_id,
    effectiveOn: data.effective_on,
    effectiveLabel: data.effective_label,
    earliestEffectiveOn: data.earliest_effective_on,
    rule: data.rule,
    approvalReference: data.approval_reference,
    submittedOn: data.submitted_on,
    submittedBy: data.submitted_by
      ? { id: data.submitted_by.id, name: data.submitted_by.name }
      : null,
    fcaReference: data.fca_reference,
    approvedOn: data.approved_on,
    registerCheck: data.register_check ?? null,
    groups: (data.groups ?? []).map((group) => ({
      label: group.label,
      missing: group.missing,
      fields: group.fields.map((field) => ({
        key: field.key,
        label: field.label,
        type: field.type,
        value: field.value,
        displayValue: field.display_value,
        missing: field.missing,
      })),
    })),
    notices: data.notices ?? [],
    values: data.values ?? null,
    actions: data.actions,
    createdAt: data.created_at,
  }
}

export const filingQueries = {
  list: (filters: FilingFilters = {}) =>
    queryOptions({
      queryKey: apiKey("/filings", filters),
      queryFn: async ({ signal }): Promise<FilingPage> => {
        const response = await apiFetch<
          ApiPage<ApiFiling> & {
            meta: {
              statuses: { key: FilingStatus; label: string }[]
              empty_label: string
            }
          }
        >("/filings", {
          params: { status: filters.status, page: filters.page },
          signal,
        })

        return {
          items: response.data.map(toFiling),
          total: response.meta.total,
          currentPage: response.meta.current_page,
          lastPage: response.meta.last_page,
          statuses: response.meta.statuses,
          emptyLabel: response.meta.empty_label,
        }
      },
    }),

  /** The firm's appointment notification and its people's Form As. */
  forFirm: (firmId: string) =>
    queryOptions({
      queryKey: apiKey(`/firms/${firmId}/filings`),
      queryFn: async ({ signal }) => {
        const { data } = await apiFetch<ApiResource<ApiFiling[]>>(
          `/firms/${firmId}/filings`,
          { signal }
        )

        return data.map(toFiling)
      },
    }),

  detail: (filingId: string) =>
    queryOptions({
      queryKey: apiKey(`/filings/${filingId}`),
      queryFn: async ({ signal }) => {
        const { data } = await apiFetch<ApiResource<ApiFiling>>(
          `/filings/${filingId}`,
          { signal }
        )

        return toFiling(data)
      },
    }),
}

export function useSubmitFiling(filingId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      submittedOn,
      fcaReference,
      effectiveOn,
    }: FilingSubmissionInput) => {
      const { data } = await apiFetch<ApiResource<ApiFiling>>(
        `/filings/${filingId}/submission`,
        {
          method: "PUT",
          body: {
            submitted_on: submittedOn,
            ...(fcaReference === "" ? {} : { fca_reference: fcaReference }),
            ...(effectiveOn === undefined ? {} : { effective_on: effectiveOn }),
          },
        }
      )

      return toFiling(data)
    },
    onSuccess: (filing) => {
      queryClient.setQueryData(apiKey(`/filings/${filing.id}`), filing)
    },
  })
}

/**
 * The last approval of a firm's onboarding makes the firm Active, and a
 * Form A's approval approves the person's appointment.
 */
export function useRecordFilingApproval(filingId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ approvedOn, reference }: FilingApprovalInput) => {
      const { data } = await apiFetch<ApiResource<ApiFiling>>(
        `/filings/${filingId}/approval`,
        { method: "PUT", body: { approved_on: approvedOn, reference } }
      )

      return toFiling(data)
    },
    onSuccess: (filing) => {
      queryClient.setQueryData(apiKey(`/filings/${filing.id}`), filing)
    },
  })
}
